import { ReactNode, useEffect, useRef, useState } from 'react';

const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const dangerBtn =
  'rounded-md bg-admin-danger px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm hover:bg-admin-surface-alt disabled:opacity-50';

interface ConfirmDialogProps {
  title: string;
  /** Body copy. Pass a string, or richer markup (e.g. the item name in bold). */
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Label shown on the confirm button while onConfirm is in flight. */
  busyLabel?: string;
  /** Red confirm button for destructive actions. */
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  busyLabel = 'A processar…',
  danger = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Default focus to Cancel so an accidental Enter/Space doesn't destroy anything.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const confirm = async (): Promise<void> => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-dialog-title" className="mb-2 text-lg font-semibold">
          {title}
        </h2>
        <div className="mb-5 text-sm text-admin-text-soft">{message}</div>
        <div className="flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={busy}
            className={secondaryBtn}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              void confirm();
            }}
            disabled={busy}
            className={danger ? dangerBtn : primaryBtn}
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
